/**
 * Palto Cards: product images fill their image area (2026-10-16).
 *
 * Why: most product photos are uploaded as square files with a large
 * transparent margin baked in (e.g. 900x900 with the product using only the
 * middle ~70% of the height). object-fit: contain fits the whole FILE into
 * the image area, margin included, so the product itself ends up small. The
 * margin differs per file (one Yu-Gi-Oh! photo has none at all), so a fixed
 * crop or scale would cut some products off.
 *
 * What: for every product image in scope this reads the picture's own
 * pixels once (a small canvas copy, no extra download for same-origin
 * images), finds the box of the actual product (pixels that are neither
 * transparent nor - on white-background photos - white) and sizes/positions
 * the <img> so that THIS box fits the image area:
 *   - product cards: the product fills ~88% of the image area,
 *   - product page: the product grows by ~12%, never beyond the image box.
 * Only transparent/white margin can end up outside the area (clipped by the
 * existing overflow: hidden); the product is always fully visible and keeps
 * its aspect ratio (object-fit: contain stays in place). The <img> keeps a
 * real layout size, so the browser picks a sharp enough source.
 *
 * Results are cached per image file (memory + localStorage), so later pages
 * apply them immediately. Any failure leaves the image exactly as before.
 * Styles: snippets/palto-image-fit.liquid.
 */

const SCOPE = [
  ":is([data-testid='product-list'], [data-testid='product-grid'], [data-testid='resource-list-grid']) .card-gallery .product-media-container img.product-media__image",
  '.product-information__media .product-media-container:not(.dialog-zoomed-gallery *) img.product-media__image',
].join(', ');

const CARD_FILL = 0.88; // share of the image area the product may fill on cards
const CARD_MAX_UPSCALE = 2; // never enlarge a picture more than 2x its current size
const PAGE_GROWTH = 1.12; // product page: ~12% larger than before
const SAMPLE_SIZE = 200; // px, longest side of the analysis copy
const STORAGE_PREFIX = 'palto-fit:v1:';

/** @type {Map<string, {a: number, l: number, t: number, r: number, b: number} | null>} */
const cache = new Map();
/** @type {WeakSet<Element>} */
const observed = new WeakSet();

/** @param {HTMLImageElement} img */
function fileKey(img) {
  try {
    return new URL(img.currentSrc || img.src, location.href).pathname;
  } catch {
    return '';
  }
}

/** @param {string} key */
function readStored(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

/** @param {string} key @param {object | null} value */
function writeStored(key, value) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // storage full or blocked - the in-memory cache still works
  }
}

/**
 * Reads the image's pixels into a small canvas. Same-origin images (the
 * live shop serves them from its own /cdn/ path) are read directly; for a
 * cross-origin source the file is fetched with CORS instead.
 * @param {HTMLImageElement} img
 */
async function readPixels(img) {
  const scale = Math.min(1, SAMPLE_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  // Decode off the main thread first (2026-10-19); otherwise drawImage
  // decodes the full-size picture synchronously and blocks the page.
  try {
    await img.decode();
  } catch {
    // not decodable this way - drawImage below still tries
  }
  // Each picture is read in a task of its own, so several images finishing
  // at once don't add up to one long task that blocks taps and scrolling.
  await new Promise((resolve) => setTimeout(resolve, 0));

  try {
    context.drawImage(img, 0, 0, width, height);
    return { data: context.getImageData(0, 0, width, height).data, width, height };
  } catch {
    // tainted canvas (cross-origin without CORS): read the file via fetch instead
  }

  const response = await fetch(img.currentSrc || img.src, { mode: 'cors' });
  if (!response.ok) return null;
  const bitmap = await createImageBitmap(await response.blob());
  context.clearRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  return { data: context.getImageData(0, 0, width, height).data, width, height };
}

/**
 * Box of the actual product inside the picture, as fractions of the
 * picture's size (l/t/r/b = empty share on each side).
 * @param {HTMLImageElement} img
 */
async function measure(img) {
  const pixels = await readPixels(img);
  if (!pixels) return null;
  const { data, width, height } = pixels;

  const isWhite = (/** @type {number} */ i) => data[i + 3] > 240 && data[i] > 242 && data[i + 1] > 242 && data[i + 2] > 242;
  // White counts as background only for photos on a white backdrop (all four corners opaque white).
  const corners = [0, (width - 1) * 4, (height - 1) * width * 4, (height * width - 1) * 4];
  const whiteBackdrop = corners.every(isWhite);

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 16) continue;
      if (whiteBackdrop && isWhite(i)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;

  // 1px safety margin at analysis resolution, so no product edge is ever clipped.
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(width - 1, maxX + 1);
  maxY = Math.min(height - 1, maxY + 1);

  return {
    a: img.naturalWidth / img.naturalHeight,
    l: minX / width,
    t: minY / height,
    r: 1 - (maxX + 1) / width,
    b: 1 - (maxY + 1) / height,
  };
}

/**
 * Sizes and positions the <img> inside its image area so the product box
 * lands where it should. Percentages keep it right on any later resize.
 * @param {HTMLImageElement} img
 * @param {{a: number, l: number, t: number, r: number, b: number}} fit
 */
function apply(img, fit) {
  const area = img.closest('.product-media-container');
  if (!area) return;
  const { width: W, height: H } = area.getBoundingClientRect();
  if (!W || !H) return;

  const contentW = 1 - fit.l - fit.r;
  const contentH = 1 - fit.t - fit.b;
  const containedWidth = Math.min(W, H * fit.a); // the picture's width with plain object-fit: contain
  const onProductPage = Boolean(img.closest('.product-information__media'));

  let imageWidth;
  if (onProductPage) {
    // grow by PAGE_GROWTH, but the product may at most reach the image box's own edges
    imageWidth = Math.min(containedWidth * PAGE_GROWTH, W / contentW, (H * fit.a) / contentH);
    imageWidth = Math.max(imageWidth, containedWidth);
  } else {
    imageWidth = Math.min(
      (CARD_FILL * W) / contentW,
      (CARD_FILL * H * fit.a) / contentH,
      containedWidth * CARD_MAX_UPSCALE
    );
  }
  const imageHeight = imageWidth / fit.a;

  // centre the product (not the file) in the image area
  const left = W / 2 - (fit.l + contentW / 2) * imageWidth;
  const top = H / 2 - (fit.t + contentH / 2) * imageHeight;

  img.style.setProperty('--pf-w', `${(imageWidth / W) * 100}%`);
  img.style.setProperty('--pf-h', `${(imageHeight / H) * 100}%`);
  img.style.setProperty('--pf-x', `${(left / W) * 100}%`);
  img.style.setProperty('--pf-y', `${(top / H) * 100}%`);
  img.dataset.paltoFit = 'on';
}

/** Image areas whose shape changes (e.g. rotating a phone) are re-applied. */
const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    for (const img of entry.target.querySelectorAll('img[data-palto-fit="on"]')) {
      const fit = cache.get(fileKey(/** @type {HTMLImageElement} */ (img)));
      if (fit) apply(/** @type {HTMLImageElement} */ (img), fit);
    }
  }
});

/** @param {HTMLImageElement} img */
async function process(img) {
  if (!img.matches(SCOPE)) return;
  const key = fileKey(img);
  if (!key) return;

  try {
    let fit = cache.get(key);
    if (fit === undefined) fit = readStored(key);
    if (fit === undefined) {
      if (!img.complete || !img.naturalWidth) return; // the load event brings it back
      fit = await measure(img);
      writeStored(key, fit);
    }
    cache.set(key, fit ?? null);

    if (fit) {
      apply(img, fit);
      const area = img.closest('.product-media-container');
      if (area && !observed.has(area)) {
        observed.add(area);
        resizeObserver.observe(area);
      }
    } else {
      img.dataset.paltoFit = 'none';
    }
  } catch {
    img.dataset.paltoFit = 'none';
  }
}

// Every image load (including ones added later by filters, carousels or
// variant changes) passes through here; load events don't bubble, so capture.
document.addEventListener(
  'load',
  (event) => {
    if (event.target instanceof HTMLImageElement) process(event.target);
  },
  true
);

for (const img of document.querySelectorAll(SCOPE)) {
  process(/** @type {HTMLImageElement} */ (img));
}
